import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (res.ok) {
      const dest = location.state?.from || (res.user.role === "super_admin" || res.user.role === "admin" ? "/admin" : "/app");
      navigate(dest, { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <AuthShell title="Welcome back." subtitle="Pick up where you left off.">
      <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
        <Field label="Email">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            inputMode="email"
            data-testid="login-email"
            className="w-full bg-transparent border-b border-[var(--line)] focus:border-[var(--brand)] outline-none py-2 text-base"
          />
        </Field>
        <Field label="Password">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            data-testid="login-password"
            className="w-full bg-transparent border-b border-[var(--line)] focus:border-[var(--brand)] outline-none py-2 text-base"
          />
        </Field>
        {error && <p className="text-sm text-[var(--err)]" data-testid="login-error">{error}</p>}
        <button type="submit" disabled={submitting} className="btn-brand w-full disabled:opacity-60" data-testid="login-submit">
          {submitting ? "Signing in…" : "Sign in"}
        </button>
        <div className="flex items-center justify-between text-sm pt-2">
          <Link to="/forgot-password" className="text-[var(--ink-secondary)] hover:text-[var(--ink-primary)]" data-testid="login-forgot-link">Forgot password?</Link>
          <Link to="/signup" className="text-[var(--brand)] hover:text-[var(--brand-hover)]" data-testid="login-signup-link">Start a free trial</Link>
        </div>
      </form>
    </AuthShell>
  );
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen flex bg-[var(--canvas)]">
      <div className="hidden md:flex md:flex-1 bg-[var(--warm-accent)] relative overflow-hidden items-end p-12">
        <div className="relative z-10">
          <Link to="/" className="font-display text-3xl tracking-tight block mb-8" data-testid="auth-logo">
            Screenplay <span className="text-[var(--brand)]">·</span> Tool
          </Link>
          <p className="font-display text-3xl leading-tight max-w-md italic text-[var(--ink-primary)]">
            &ldquo;Write your screenplay the way you&rsquo;d write a letter to a friend who happens to make movies.&rdquo;
          </p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-sm">
          <div className="md:hidden mb-10">
            <Link to="/" className="font-display text-2xl tracking-tight" data-testid="auth-logo-mobile">
              Screenplay <span className="text-[var(--brand)]">·</span> Tool
            </Link>
          </div>
          <h1 className="font-display text-4xl mb-2 text-[var(--ink-primary)]" data-testid="auth-title">{title}</h1>
          {subtitle && <p className="text-[var(--ink-secondary)] mb-8" data-testid="auth-subtitle">{subtitle}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-widest text-[var(--ink-muted)] mb-1.5">{label}</span>
      {children}
    </label>
  );
}

export { Field };
